import { Injectable } from '@angular/core';
import { AndroWebCoreService } from '@app/core/AndroWebCoreService';
import { OpeningDaysAndTime } from '@app/models/opening-days-and-time';
import { Site } from '@app/models/site';
import { ServiceHelper } from '@app/shared/services/service.helper';
import { Product } from '@app/models/menu/product';
import { Menu } from '@app/models/menu/menu';
import { ConfigurationService } from '@app/app-initialisers/configuration-service/configuration.service';
import { NavigatorService } from '@app/core/navigator.service';
import { OrderOccasion } from '@app/models/order-occasion';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { Tenant } from '@app/models/tenant';
import { Deal } from '@app/models/menu/deal';
import { MenuService } from '@app/api/menu.service';
import { StructuredMenu } from '@app/models/structured-data/structured-menu';
import { StructuredOpeningHours } from '@app/models/structured-data/structured-opening-hours';
import { StructuredOrganizationData } from '@app/models/structured-data/structured-organization-data';
import { StructuredRestaurantData } from '@app/models/structured-data/structured-restaurant-data';
import { MenuDealHelperService } from '@app/shared/services/menu-deal-helper/menu-deal-helper.service';
import { MenuHelperService } from '@app/shared/services/menu-helper/menu-helper.service';

@Injectable({
  providedIn: 'root'
})
export class StructuredDataService extends AndroWebCoreService {
  public readonly scriptType: string = 'application/json+ld';

  constructor(
    private _menuService: MenuService,
    private _serviceHelper: ServiceHelper,
    private _navigatorService: NavigatorService,
    private _menuHelperService: MenuHelperService,
    private _configurationService: ConfigurationService,
    private _menuDealHelperService: MenuDealHelperService
  ) {
    super();
  }

  /**
   * Builds the Json-ld structure
   * @param {Site[]} sites
   * @return {*}  {string}
   * @memberof JsonStructuredDataService
   */
  public buildTenantStructuredData(sites: Site[]): string | null {
    if (!sites) {
      return null;
    }

    try {
      const currentUrl: string = this.getCurrentUrl();
      const tenant: Tenant = this.getCurrentTenant();
      const getMenuUrl = (site: Site) => this._navigatorService.generateRoutePath(
          this._navigatorService.getLocationBySiteId(site.Id),
          site.OccasionsSupported[0],
          NavigationSectionType.Menu
      );

      const storeLocations: StructuredRestaurantData[] = sites.map((site: Site) => {
        const result: StructuredRestaurantData = {
          '@type': 'Restaurant',
          'name': site.Name,
          'telephone': site.Phone,
          'geo': {
            '@type': 'GeoCoordinates',
            'latitude': site.Address?.Latitude,
            'longitude': site.Address?.Longitude
          },
          'priceRange': '£5 - £25',
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': `${site.Address.Line1} ${site.Address.Line2 || ''}`.trim(),
            'addressLocality': site.Address.Town,
            'addressRegion': site.Address.State,
            'postalCode': site.Address.Postcode,
            'addressCountry': site.Address.Country
          },
          'menu': encodeURI(`${currentUrl}/${getMenuUrl(site)}`),
          'openingHoursSpecification': this.getOpeningTimes(this._serviceHelper.getOpeningDatesAndTimes(site.OpeningHours)),
          'image': tenant.Branding?.LogoImageUrl
        };

        const cuisines: string = tenant.PageSeos?.[0]?.Keywords;
        if (cuisines) {
          result.servesCuisine = cuisines;
        }

        if (site.OccasionsSupported.includes(OrderOccasion.Delivery)) {
          result.hasDelivery = true;
          result.deliveryArea = site.DeliveryZone?.Units.map((unit) => ({
            '@type': 'Place',
            'postalCode': unit.Code
          })) || [];

          if (site.EstimatedDeliveryTime) {
            result.deliveryTime = this.getEstimatedOccasionTime(site.EstimatedDeliveryTime);
          }
        }

        if (site.OccasionsSupported.includes(OrderOccasion.Collection) && site.EstimatedCollectionTime) {
          result.collectionTime = this.getEstimatedOccasionTime(site.EstimatedCollectionTime);
        }

        return result;
      });

      const organization: StructuredOrganizationData = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': tenant.Name,
        'logo': tenant.Branding?.LogoImageUrl,
        'url': currentUrl,
        'location': storeLocations
      };

      return JSON.stringify(organization, null, 2);
    } catch (error) {
      this.trackException(error, false);
      return null;
    }
  }

  /**
   * Creates the menu structured data
   * @param {DisplayMenu} menu
   * @return {*}  {string}
   */
  public buildMenuStructuredData(menu: Menu, occasion: OrderOccasion): string | null {
    if (!menu) {
      return null;
    }

    try {
      const structuredMenu: StructuredMenu = {
        '@context': 'https://schema.org',
        '@type': 'Menu',
        'inLanguage': 'English',
        'hasMenuSection': menu.Sections.map((section) => ({
          '@type': 'MenuSection',
          'name': section.Name,
          'description': section.Description,
          'image': section.ImageBase ? `${section.ImageBase}/landscape-small.jpg` : null,
          'hasMenuItem': [
            ...section.Products.flatMap((productId: string) => {
              const product: Product | undefined = menu.Products.find((p: Product) => p.Id === productId);

              if (!product) {
                return [];
              }

              return product.Variants.map((variant) => {
                const variantPrice = variant.Prices.find((price) => price.Occasion === occasion);
                const optionNames = this._menuHelperService.getVariantOptionsPathText(variant, this._menuService.productOptionsById);

                const result: any = {
                  '@type': 'MenuItem',
                  'name': `${product.Name} ${optionNames}`,
                  'description': product.Description,
                  'image': product.ImageBase ? `${product.ImageBase}/landscape-small.jpg` : null
                };

                if (variantPrice) {
                  result.offers = {
                    '@type': 'Offer',
                    'price': variantPrice.Amount,
                    'priceCurrency': 'GBP'
                  };
                }

                if (variant.Nutrition) {
                  result.nutrition = {
                    '@type': 'NutritionInformation',
                    'calories': variant.Nutrition.Calories,
                    'servingSize': variant.Nutrition.PortionDescription || `${variant.Nutrition.NumberOfPortions} servings`
                  };
                }

                if (product.Allergens) {
                  result.additionalProperty = [
                    product.Allergens.Contains ? {
                      '@type': 'PropertyValue',
                      'name': 'Contains',
                      'value': this._menuHelperService.getFriendlyAllergenValue(product.Allergens.Contains)
                    } : undefined,
                    product.Allergens.MayContain ? {
                      '@type': 'PropertyValue',
                      'name': 'May contain',
                      'value': this._menuHelperService.getFriendlyAllergenValue(product.Allergens.MayContain)
                    } : undefined
                  ].filter((x) => !!x);
                }

                return result;
              });
            }).filter((menuItem) => !!menuItem),

            // Include the deals in the same hasMenuItem array
            ...section.Deals.map((dealId: string) => {
              const deal: Deal | undefined = menu.Deals.find((d: Deal) => d.Id === dealId);

              if (!deal || deal.RequiresUnlock) {
                return null;
              }

              return {
                '@type': 'MenuItem',
                'name': deal.Name,
                'description': deal.Description,
                'image': deal.ImageBase ? `${deal.ImageBase}/landscape-small.jpg` : null,
                'offers': {
                  '@type': 'Offer',
                  'price': this._menuDealHelperService.getMinPriceForDeal(deal, occasion) || 0,
                  'priceCurrency': 'GBP'
                }
              };
            }).filter((dealItem) => !!dealItem)
          ]
        }))
      };

      return JSON.stringify(structuredMenu, null, 2);
    } catch (error) {
      this.trackException(error, false);
      return null;
    }
  }

  /**
   * Formats the opening times and returns an array of StructuredOpeningHours objects, handling multiple periods and next-day closing times.
   * @param {OpeningDaysAndTime[]} openingDaysAndTimes - Array of opening days and times.
   * @return {StructuredOpeningHours[]} - Array of formatted StructuredOpeningHours objects.
   */
  private getOpeningTimes(openingDaysAndTimes: OpeningDaysAndTime[]): StructuredOpeningHours[] {
    const times: StructuredOpeningHours[] = [];

    if (openingDaysAndTimes && Array.isArray(openingDaysAndTimes)) {
      openingDaysAndTimes.forEach((dateAndTime: OpeningDaysAndTime) => {
        // Process each time period for the current day
        dateAndTime.times.forEach((timePeriod) => {
          const { Start: startTime, End: endTime } = timePeriod;

          if (startTime && endTime) {
            const dayOfWeek = this.getDayOfWeek(dateAndTime.DayOfWeek);

            if (startTime === endTime) {
              // Handle 24-hour period (e.g., 06:30 - 06:30 the next day)
              times.push({
                '@type': 'OpeningHoursSpecification',
                'dayOfWeek': dayOfWeek,
                'opens': startTime,
                'closes': '23:59'
              });
              times.push({
                '@type': 'OpeningHoursSpecification',
                'dayOfWeek': this.getDayOfWeek((dateAndTime.DayOfWeek + 1) % 7), // Next day
                'opens': '00:00',
                'closes': endTime
              });
            } else if (this.isNextDayClosing(startTime, endTime)) {
              // Split into two periods if the end time is on the next day
              times.push({
                '@type': 'OpeningHoursSpecification',
                'dayOfWeek': dayOfWeek,
                'opens': startTime,
                'closes': '23:59'
              });
              times.push({
                '@type': 'OpeningHoursSpecification',
                'dayOfWeek': this.getDayOfWeek((dateAndTime.DayOfWeek + 1) % 7), // Next day
                'opens': '00:00',
                'closes': endTime
              });
            } else {
              // Normal time period within the same day
              times.push({
                '@type': 'OpeningHoursSpecification',
                'dayOfWeek': dayOfWeek,
                'opens': startTime,
                'closes': endTime
              });
            }
          }
        });
      });
    }

    return times;
  }

  /**
  * Checks if the end time is on the next day compared to the start time.
  * @param {string} startTime - The start time (e.g., "22:00").
  * @param {string} endTime - The end time (e.g., "03:15").
  * @return {boolean} - True if the end time is on the next day.
  */
  private isNextDayClosing(startTime: string, endTime: string): boolean {
    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);
    return endHour < startHour;
  }

  /**
  * Converts a numerical day representation to the corresponding day of the week in string format.
  * @private
  * @param {number} day - The numerical representation of the day.
  * @return {string} - The day of the week.
  */
  private getDayOfWeek(day: number): string {
    const daysOfWeekMap: { [key: number]: string } = {
      0: 'Monday',
      1: 'Tuesday',
      2: 'Wednesday',
      3: 'Thursday',
      4: 'Friday',
      5: 'Saturday',
      6: 'Sunday'
    };
    return daysOfWeekMap[day];
  }

  /**
 * Gets the estimated time in ISO 8601 duration format (e.g., "PT30M") for the given occasion.
 * @param {Site} site - The site to get the estimated time for.
 * @return {string} - The estimated time in ISO 8601 duration format.
 */
  private getEstimatedOccasionTime(estimatedTimeString: string): string {
    const [hours, minutes, seconds] = estimatedTimeString.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + Math.round(seconds / 60);

    return `PT${totalMinutes}M`;
  }

  private getCurrentUrl: () => string = () => window.location.href.endsWith('/') ? window.location.href.slice(0, -1) : window.location.href;
  private getCurrentTenant: () => Tenant = () => this._configurationService.tenant;
}
